import { Injectable }                   from '@angular/core';
import { BusinessPremises }             from '@app/modules/business-premises/models/business-premises.model';
import {
	BusinessPremisesDetailPageTssExternalFormPageCreateRoute,
	BusinessPremisesRoutesEnum
}                                       from '@app/modules/business-premises/routes/business-premises.routes';
import {
	Ers,
	ErsVariant
}                                       from '@app/modules/ers/models/ers.model';
import { FccInstallationTypeEnum }      from '@app/modules/fcc/models/fcc.model';
import { ImportTypeEnum }               from '@app/modules/structure-import/models/structure-import.model';
import {
	IVirtualTssMetadata,
	VIRTUAL_TSS_METADATA
}                                       from '@app/modules/tss/config/tss.config';
import {
	RequestTssModalModeEnum,
	RequestTssModalTitleEnum
}                                       from '@app/modules/tss/modals/request-tss-modal/request-tss-modal.model';
import { TssLifecycleActionsEnum }      from '@app/modules/tss/modals/tss-lifecycle-modal/tss-lifecycle-modal.model';
import {
	Tss,
	TssAssignmentTypeEnum,
	TssExternal,
	TssExternalStatusEnum,
	TssFccVersions,
	TssModeEnum,
	TssModeSubTypeEnum,
	TssModeWithSubTypeEnum,
	TssPresetEnum,
	TssRequest,
	TssStatus,
	TssStatusEnum,
	TssTagEnum,
	TssVariant
}                                       from '@app/modules/tss/models/tss.model';
import { TssDetailsConfig }             from '@app/modules/tss/presets/tss-details-config.model';
import {
	TssDetailPageBaseRoute,
	TssDetailPageRoute,
	TssDetailPageTabBaseRoute,
	TssDetailPageTabRoute,
	TssExternalDetailPageRoute,
	TssExternalDetailPageTabRoute,
	TssExternalFormPageUpdateRoute,
	TssExternalListPageRoute,
	TssFormPageUpdateRoute,
	TssListPageBaseRoute,
	TssListPageRoute,
	TssRoutesEnum
}                                       from '@app/modules/tss/routes/tss.routes';
import {
	IPropsChangeStatusTssEntity,
	IPropsTssLifecycleDelete,
	IPropsUpdateTssState,
	PropsTssLifecycleType
}                                       from '@app/modules/tss/store/tss.actions';
import { tssStore }                     from '@app/modules/tss/store/tss.store';
import { AbstractBaseService }          from '@core/bases/services/abstractBaseService';
import {
	ITssHttpParams,
	RequestCompleteEntityState,
	RequestCompleteListState,
	RequestState
}                                       from '@core/interfaces/api.interface';
import {
	AllowedDetailsTabEnum,
	ObservableTypeEnum,
	ProductEnum
}                                       from '@core/interfaces/common.interfaces';
import {
	IRouteDefinition,
	RouteBasePath,
	RouteDefinition
}                                       from '@core/interfaces/route.interface';
import {
	IStoreConfig,
	IStoreProcessingConfig,
	StoreActionsEnum
}                                       from '@core/interfaces/store.interface';
import { TssApiService }                from '@http/tss/tss-api.service';
import { TssExternalApiService }        from '@http/tss/tss-external-api.service';
import { EntityVariantEnum }            from '@models/entity.model';
import { PresetNavigateBackConfig }     from '@models/preset';
import { TssScopesEnum }                from '@models/scope.enums';
import {
	select,
	Store
}                                       from '@ngrx/store';
import {
	IChangeStatusTssCallbackOptions,
	IStatusFlowChangeEvent
}                                       from '@shared/components/status-flow/status-flow.model';
import { StatusFlowService }            from '@shared/components/status-flow/status-flow.service';
import { FilterIdentifiersEnum }        from '@shared/list-operations/model/filter.model';
import { ListOperationsIdentifierEnum } from '@shared/list-operations/model/list-operations.model';
import { actionStatus }                 from '@shared/services/toaster.service';
import {
	EntityUtils,
	ObjectUtils
}                                       from '@utils/helpers';
import {
	Observable,
	of,
	Subject
}                                       from 'rxjs';
import {
	map,
	switchMap,
	take
}                                       from 'rxjs/operators';


@Injectable( {
	providedIn: 'root'
} )
export class TssService extends AbstractBaseService {

	private readonly _tssMetadata: IVirtualTssMetadata = VIRTUAL_TSS_METADATA;

	constructor(
		private readonly _statusFlowService: StatusFlowService,
		private readonly _tssApiService: TssApiService,
		private readonly _tssExternalApiService: TssExternalApiService,
		protected readonly store$: Store
	) {
		super( store$, tssStore.selectors, tssStore.actions );
		this.logger.trace( 'TssService:constructor' );
		this.updateTssOrderIndex();
	}

	// TSS SPECIFIC SELECTORS ###########################################################################
	selectTSSForErs$( tssId: string, entityVariant: EntityVariantEnum ): Observable<TssVariant> {
		return this.store$.pipe( select<Record<string, TssVariant>, TssVariant>( tssStore.selectors.selectTSSForErs( tssId, entityVariant ) ) );
	}

	selectAllTssAssignedToErs$( tssEntityId: string ): Observable<TssVariant[]> {
		return this.store$.pipe( select<Record<string, TssVariant[]>, TssVariant[]>( tssStore.selectors.selectAllTssAssignedToErs( tssEntityId ) ) );
	}

	selectTssByIdAndEntityVariant$( tssId: string, entityVariant: EntityVariantEnum ): Observable<TssVariant> {
		return this.store$.pipe( select<Record<string, TssVariant>, TssVariant>( tssStore.selectors.selectTssByIdAndEntityVariant( tssId, entityVariant ) ) );
	}

	selectTSSForBP$( businessPremisesId: string, entityVariant?: EntityVariantEnum ): Observable<TssVariant[]> {
		return this.store$.pipe( select<Record<string, TssVariant[]>, TssVariant[]>( tssStore.selectors.selectTSSForBP( businessPremisesId, entityVariant ) ) );
	}

	selectAllTssWithBusinessPremisesId$( businessPremisesId: string, entityVariant?: EntityVariantEnum ): Observable<TssVariant[]> {
		return this.store$.pipe(
			select<Record<string, TssVariant[]>, TssVariant[]>( tssStore.selectors.selectAllTssWithBusinessPremisesId( businessPremisesId, entityVariant ) ) );
	}

	selectListByEntityVariant$( entityVariant: EntityVariantEnum ): Observable<TssVariant[]> {
		return this.store$.pipe( select<Record<string, TssVariant[]>, TssVariant[]>( tssStore.selectors.selectTssListByEntityVariant( entityVariant ) ) );
	}

	selectHoveredTssId$(): Observable<string> {
		return this.store$.pipe( select<Record<string, string>, string>( tssStore.selectors.hoveredTssId() ) );
	}


	// TSS State ###########################################################################
	updateStoreState( updateTssState: IPropsUpdateTssState ): void {
		this.store$.dispatch( tssStore.actions.updateTssState( updateTssState ) );
	}

	// HELPERS ################################################################################
	getTssPresetByErsVariant( ers: ErsVariant ): TssPresetEnum {
		switch ( ers.entityVariant ) {
			case EntityVariantEnum.EXTERNAL:
				return TssPresetEnum.TSS_EXTERNAL;

			case EntityVariantEnum.INTERNAL:
				return TssPresetEnum.TSS;

			default:
				return null;
		}
	}

	isInStatusState( entityVariant: EntityVariantEnum, status: TssStatus, variantsStatusMap: Map<EntityVariantEnum, TssStatus> ): boolean {
		if ( !status || !entityVariant || !variantsStatusMap ) { return false; }

		const statusEnum: TssStatus = variantsStatusMap.get( entityVariant );
		return statusEnum === status;
	}

	isInStatusStateActive( entityVariant: EntityVariantEnum, status: TssStatus ): boolean {
		if ( !entityVariant || !status ) { return false; }


		const variantsStatusMap: Map<EntityVariantEnum, TssStatus> =
			new Map<EntityVariantEnum, TssStatus>( [
				[ EntityVariantEnum.EXTERNAL, TssExternalStatusEnum.ACTIVE ],
				[ EntityVariantEnum.INTERNAL, TssStatusEnum.ACTIVE ]
			] );

		return this.isInStatusState( entityVariant, status, variantsStatusMap );
	}

	isInStatusStateDisabled( entityVariant: EntityVariantEnum, status: TssStatus ): boolean {
		if ( !entityVariant || !status ) { return false; }

		const variantsStatusMap: Map<EntityVariantEnum, TssStatus> =
			new Map<EntityVariantEnum, TssStatus>( [
				[ EntityVariantEnum.EXTERNAL, TssExternalStatusEnum.DISABLED ],
				[ EntityVariantEnum.INTERNAL, TssStatusEnum.DISABLED ]
			] );

		return this.isInStatusState( entityVariant, status, variantsStatusMap );
	}

	getTssTags( tss: TssVariant ): string[] {
		const tags: string[] = [];

		if ( !tss ) { return tags; }

		if ( 'fccInstallationType' in tss ) {
			if (
				this.authorizationService.hasPermission( TssScopesEnum.ACCESS_FCC_CONTENT ) &&
				tss?.fccInstallationType === FccInstallationTypeEnum.FCC_DATACENTER
			) {
				tags.push( TssTagEnum.FCC_DATACENTER );
			}
		}

		if ( 'mode' in tss ) {
			tags.push( TssTagEnum.MODE );
		}

		return tags;
	}

	private updateTssOrderIndex(): void {
		function groupByBpId( tssList: TssVariant[] ): { [ key: string ]: TssVariant[] } {
			let groupedTss: { [ key: string ]: TssVariant[] } = {};
			tssList.forEach( ( tss: TssVariant ) => {
				if ( groupedTss[ tss.businessPremisesId ] ) {
					groupedTss = {
						...groupedTss,
						[ tss.businessPremisesId ]: [ ...groupedTss[ tss.businessPremisesId ], tss ]
					};
				} else {
					groupedTss = {
						...groupedTss,
						[ tss.businessPremisesId ]: [ tss ]
					};
				}
			} );
			return groupedTss;
		}

		/**
		 * Hardcoded ordering to prevent some issues regarding different Tss entities in store (e.g. when some Tss are filtered out because of DISABLED).
		 * Current order assure that all usages (TSS chips and TSS chips in lists) are in proper sync after all possible operations (all refresh, BP config,
		 * etc...).
		 *
		 * @param tssStatus status of tss item
		 */
		function orderByStatusPhysicalEnd( tssStatus: TssStatusEnum ): number {
			switch ( tssStatus ) {

				case TssStatusEnum.DISABLED:
					return 2;
				case TssStatusEnum.DELETED:
					return 3;

				default:
					return 1;
			}
		}

		function orderByTssMode( tssMode: TssModeEnum ): number {
			switch ( tssMode ) {

				case TssModeEnum.CLOUD:
					return 1;

				case TssModeEnum.TOKEN:
					return 2;

				case TssModeEnum.SWISSBIT_TOKEN:
					return 3;

				case TssModeEnum.OFFLINE:
					return 4;

				default:
					return 5;
			}
		}

		function orderByEntityVariant( entityVariant: EntityVariantEnum ): number {
			switch ( entityVariant ) {

				case EntityVariantEnum.EXTERNAL:
					return 2;

				case EntityVariantEnum.INTERNAL:
					return 1;

				default:
					return 3;
			}
		}

		let prevTssList: string;

		this.storeList$<TssVariant>()
		.subscribe( ( tssList: TssVariant[] ) => {
			const newTssList =
				JSON.stringify(
					tssList.map( ( tss: TssVariant ) => {
						const assignmentType: TssAssignmentTypeEnum = 'assignmentType' in tss ? tss.assignmentType : null;
						const mode: TssModeEnum | string = 'mode' in tss ? tss.mode : null;

						return {
							assignmentType,
							id: tss.id,
							mode,
							orderIndex: tss.orderIndex,
							status: tss.status
						};
					} )
				);

			// skip if tssList is unchanged
			if ( prevTssList === newTssList ) { return; }
			prevTssList = newTssList;

			// creates tss groups by bp id
			const bpGroupedTssList = Object.values( groupByBpId( tssList ) );
			const orderIndexUpdatedTssList = [];

			// sorts each group by multiple fields criteria
			Object.values( bpGroupedTssList ).forEach( ( groupedTssList: TssVariant[] ) => {
				const fieldSortedAndIndexesGroupList = [ ...groupedTssList ].sort(
					ObjectUtils.sortByObjectValuesCompareFnGenerator<Tss>( [
						{ key: 'entityVariant', map: ( entityVariant: EntityVariantEnum ) => orderByEntityVariant( entityVariant ) },
						{ key: 'status', map: ( tssStatus: TssStatusEnum ) => orderByStatusPhysicalEnd( tssStatus ) },
						{ key: 'assignmentType' },
						{ key: 'status' },
						{ key: 'mode', map: ( tssMode: TssModeEnum ) => orderByTssMode( tssMode ) },
						{ key: 'id' }
					] )
				)
				// applies orderIndex
				.map( ( tss: TssVariant, index: number ) =>
					Object.assign( {}, tss, { orderIndex: index + 1 } )
				);

				// adds sorted and indexed group items
				orderIndexUpdatedTssList.push( ...fieldSortedAndIndexesGroupList );
			} );

			// save to store
			if ( orderIndexUpdatedTssList.length ) {
				this.storeUpsertMany<TssVariant>( orderIndexUpdatedTssList, 'TssChipsComponent:updateTssOrderIndex' );
			}
		} );
	}

	getTssModeWithSubtype( mode: TssModeEnum, modeSubtype: TssModeSubTypeEnum ): TssModeWithSubTypeEnum {
		switch ( mode ) {
			case TssModeEnum.CLOUD:
				return TssModeWithSubTypeEnum.CLOUD;

			case TssModeEnum.TOKEN:
				switch ( modeSubtype ) {
					case TssModeSubTypeEnum.CV1:
						return TssModeWithSubTypeEnum.TOKEN_CV1;

					case TssModeSubTypeEnum.CV2:
						return TssModeWithSubTypeEnum.TOKEN_CV2;

					default:
						return TssModeWithSubTypeEnum.TOKEN;
				}

			case TssModeEnum.SWISSBIT_TOKEN:
				return TssModeWithSubTypeEnum.SWISSBIT_TOKEN;

			case TssModeEnum.OFFLINE:
				return TssModeWithSubTypeEnum.OFFLINE;
		}
	}

	requestTssForBusinessPremisesAfterCreate( businessPremises: BusinessPremises, caller: string ): void {
		const titleType = RequestTssModalTitleEnum.AFTER_CREATE;
		this.requestTssForBusinessPremises( businessPremises, titleType, caller );
	}

	requestTssForBusinessPremisesByUser( businessPremises: BusinessPremises, caller: string ): void {
		const titleType = RequestTssModalTitleEnum.ON_USER_REQUEST;
		this.requestTssForBusinessPremises( businessPremises, titleType, caller );
	}

	requestTssForErsAfterCreate( businessPremises: BusinessPremises, ers: Ers, caller: string ): void {
		const titleType = RequestTssModalTitleEnum.AFTER_CREATE;
		this.requestTssForErs( businessPremises, ers, titleType, caller );
	}

	requestTssForErsByUser( businessPremises: BusinessPremises, ers: ErsVariant, caller: string ): void {
		const titleType = RequestTssModalTitleEnum.ON_USER_REQUEST;
		this.requestTssForErs( businessPremises, ers, titleType, caller );
	}

	rehydrateTssListByBusinessPremisesId(
		options: {
			businessPremisesId: string,
			entityVariant: EntityVariantEnum,
			presetId: TssPresetEnum
		}
	): Observable<RequestCompleteListState<TssVariant>> {
		const {
			businessPremisesId,
			entityVariant,
			presetId
		} = options;

		const req$ = this.listByBusinessPremisesId( {
			id: businessPremisesId,
			params: {
				activeFilters: {
					[ FilterIdentifiersEnum.STATUS ]: {
						param: this._tssMetadata.statusAllowedToListTssUnderBusinessPremises( entityVariant )
					}
				}
			},
			storeConfig: { updateStore: true },
			getAllPagesData: true,
			presetId
		} );

		const subject = new Subject<RequestCompleteListState<TssVariant>>();

		req$.pipe( take( 1 ) )
		.subscribe( ( listState: RequestCompleteListState<TssVariant> ) => {
			const tssList: TssVariant[] = listState.list ?? [];

			if ( tssList.length ) {
				this.storeUpsertMany<TssVariant>( tssList, 'TssService:rehydrateTssListByBusinessPremisesId' );

				// removes on BE deleted Items from store
				this.selectAllTssWithBusinessPremisesId$( businessPremisesId, entityVariant )
				.pipe( take( 1 ) )
				.subscribe( ( tssListFromStore: TssVariant[] ) => {
					// check if store holds more tsses as exist on BE
					if ( tssList.length < tssListFromStore.length ) {
						// get all tsses which needs to be removed from store
						const tssToDeleteFromStore = tssListFromStore.filter( ( filteredTss: TssVariant ) =>
							!tssList.find( ( tss: TssVariant ) => tss.id === filteredTss.id )
						);

						// removes found tsses from store
						this.storeRemoveMany( tssToDeleteFromStore );
					}
				} );
			} else {
				this.selectAllTssWithBusinessPremisesId$( businessPremisesId, entityVariant )
				.pipe( take( 1 ) )
				.subscribe( ( tssListFromStore: TssVariant[] ) => {
					// removes all bp related tsses from store
					this.storeRemoveMany( tssListFromStore );
				} );
			}

			subject.next( listState );
			subject.complete();
		} );

		return subject;
	}

	// EVENTS #################################################################################
	private deleteCallback(
		service: TssService,
		callbackOptions: IChangeStatusTssCallbackOptions
	): void {
		const {
			caller,
			entity,
			navigateToList
		} = callbackOptions;

		const tss: TssVariant = entity as TssVariant;

		service.storeRemoveOne<TssVariant>( tss?.id );
		if ( navigateToList ) { service.navigateBack( { caller } ); }
	}

	private deleteTss(
		options: {
			tss: TssVariant,
			caller: string,
			listOperationsId?: ListOperationsIdentifierEnum,
			navigateToList?: boolean,
			presetId: TssPresetEnum
		}
	): void {
		const {
			caller,
			listOperationsId,
			navigateToList,
			presetId,
			tss
		} = options;

		const tssLifecycleDeleteProps: IPropsTssLifecycleDelete = {
			callback: this.deleteCallback,
			callbackOptions: {
				entity: tss,
				listOperationsId,
				navigateToList,
				presetId
			},
			caller: `${ caller } | TssService:deleteTss`,
			lifecycleAction: TssLifecycleActionsEnum.DELETE,
			targetStatus: EntityUtils.getValueByEntityVariant<TssExternalStatusEnum.DELETED, TssStatusEnum.DELETED>(
				tss.entityVariant,
				TssExternalStatusEnum.DELETED,
				TssStatusEnum.DELETED
			)
		};

		this.store$.dispatch(
			tssStore.actions.showTssLifecycle( tssLifecycleDeleteProps )
		);
	}

	deleteTssOnDetailPage(
		options: {
			caller: string,
			presetId: TssPresetEnum,
			tss: TssVariant
		}
	): void {
		this.deleteTss( { ...options, navigateToList: true } );
	}

	deleteTssOnEntity( options: {
			caller: string,
			listOperationsId?: ListOperationsIdentifierEnum,
			presetId: TssPresetEnum,
			tss: TssVariant
		}
	): void {
		this.deleteTss( { ...options, navigateToList: false } );
	}

	deleteTssOnTssLifecycleModal( tss: TssVariant, caller: string, presetId: TssPresetEnum ): Observable<RequestCompleteEntityState<TssVariant>> {
		this.logger.trace( `${ caller } | TssService:deleteTssOnTssLifecycleModal` );
		return this.delete( { presetId, storeConfig: { updateStore: true }, tssId: tss?.id } );
	}

	private changeStatusCallback(
		service: TssService,
		callbackOptions: IChangeStatusTssCallbackOptions
	): void {
		const {
			caller,
			entity
		} = callbackOptions;

		const tss: TssVariant = entity as TssVariant;

		if ( tss ) {
			service.checkAndUpdateEntityState(
				tss,
				'TssService:changeStatusCallback->' + caller
			);
		}
	}


	private defaultStatusFlow(
		options: {
			callbackOptions: IChangeStatusTssCallbackOptions,
			statusChangeEvent: IStatusFlowChangeEvent<TssStatus>
		}
	): void {
		const {
			callbackOptions,
			statusChangeEvent
		} = options;


		this._statusFlowService.changeStatus(
			{
				callback: this.changeStatusCallback,
				callbackOptions,
				caller: callbackOptions?.caller,
				entity: callbackOptions?.entity,
				entityId: VIRTUAL_TSS_METADATA.entityId,
				entityService: this,
				statusChangeEvent
			}
		);
	}

	private dispatchTssLifecycle( tssLifecycleTypeProps: PropsTssLifecycleType ): void {
		this.store$.dispatch(
			tssStore.actions.showTssLifecycle( tssLifecycleTypeProps )
		);
	}

	private changeTssStatus(
		callbackOptions: IChangeStatusTssCallbackOptions,
		statusChangeEvent: IStatusFlowChangeEvent<TssStatus>
	): void {
		const tss: TssVariant = callbackOptions.entity as TssVariant;

		const changeStatusTssEntityProps: IPropsChangeStatusTssEntity = {
			callback: this.changeStatusCallback,
			callbackOptions,
			caller: callbackOptions?.caller + ' | TssService:changeTssStatus->showTssLifecycle',
			targetStatus: statusChangeEvent.targetStatus
		};


		switch ( statusChangeEvent.targetStatus ) {
			// Active case
			case EntityUtils.getValueByEntityVariant<TssExternalStatusEnum, TssStatusEnum>(
				tss.entityVariant,
				TssExternalStatusEnum.ACTIVE,
				TssStatusEnum.ACTIVE
			):
				EntityUtils.executeByEntityVariant(
					tss.entityVariant,
					() => this.dispatchTssLifecycle( {
						...changeStatusTssEntityProps,
						lifecycleAction: TssLifecycleActionsEnum.ACTIVATE
					} ),
					() => this.defaultStatusFlow( { callbackOptions, statusChangeEvent } )
				);
				return;

			// Disable case
			case EntityUtils.getValueByEntityVariant<TssExternalStatusEnum, TssStatusEnum>(
				tss.entityVariant,
				TssExternalStatusEnum.DISABLED,
				TssStatusEnum.DISABLED
			):
				EntityUtils.executeByEntityVariant(
					tss.entityVariant,
					() => this.defaultStatusFlow( { callbackOptions, statusChangeEvent } ),
					() => this.dispatchTssLifecycle( {
						...changeStatusTssEntityProps,
						lifecycleAction: TssLifecycleActionsEnum.DISABLE
					} )
				);
				return;

			default:
				this.defaultStatusFlow( { callbackOptions, statusChangeEvent } );
		}
	}

	private requestTssForBusinessPremises(
		businessPremises: BusinessPremises,
		titleType: RequestTssModalTitleEnum,
		caller: string
	): void {
		this.store$.dispatch( tssStore.actions.requestTssForBusinessPremises(
			{
				businessPremises,
				titleType,
				forEntity: RequestTssModalModeEnum.BUSINESS_PREMISES,
				caller
			} )
		);
	}

	private requestTssForErs(
		businessPremises: BusinessPremises,
		ers: ErsVariant,
		titleType: RequestTssModalTitleEnum,
		caller: string
	): void {
		this.store$.dispatch( tssStore.actions.requestTssForErs(
			{
				businessPremises,
				ers: ers as Ers,
				titleType,
				forEntity: RequestTssModalModeEnum.ERS,
				caller
			} )
		);
	}

	private requestPassword( tss: Tss, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._tssApiService.resetFccPassword( tss );
		return this.requestEntityThroughProxy<Tss>( tss.id, req$, storeProcessingConfig );
	}

	private revealPassword( tss: Tss, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._tssApiService.getFccPassword( tss );
		return this.requestEntityThroughProxy<Tss>( tss.id, req$, storeProcessingConfig );
	}


	changeTssStatusOnDetailPage(
		data: {
			tss: TssVariant,
			caller: string,
			statusChangeEvent: IStatusFlowChangeEvent<TssStatus>,
			listOperationsId?: ListOperationsIdentifierEnum,
			presetId: TssPresetEnum
		}
	): void {
		this.changeTssStatus(
			{
				caller: data?.caller,
				entity: data?.tss,
				listOperationsId: data?.listOperationsId,
				presetId: data?.presetId
			},
			data?.statusChangeEvent
		);
	}

	changeTssStatusOnEntity(
		data: {
			tss: TssVariant,
			caller: string,
			statusChangeEvent: IStatusFlowChangeEvent<TssStatus>,
			listOperationsId?: ListOperationsIdentifierEnum,
			presetId: TssPresetEnum
		}
	): void {
		this.changeTssStatus(
			{
				caller: data?.caller,
				entity: data?.tss,
				listOperationsId: data?.listOperationsId,
				presetId: data?.presetId
			},
			data?.statusChangeEvent
		);
	}

	changeTssStatusOnTssLifecycleModal(
		data: {
			presetId: TssPresetEnum,
			status: TssStatus,
			tss: TssVariant
		}
	): Observable<RequestCompleteEntityState<TssVariant>> {
		return this.changeStatus( data.tss, data.status, { updateStore: false }, data.presetId );
	}

	private pageReadTssHandler( readReq$: Observable<RequestCompleteEntityState<TssVariant>>, navigateBackFallback: PresetNavigateBackConfig ): void {
		readReq$
		.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteEntityState<TssVariant> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			// on fail redirects to list
			if ( tssState.requestState === RequestState.FAILED ) {
				this.toasterService.dispatch( tssState, this._tssMetadata.entityId, actionStatus.READ );
				this.navigateBack( { caller: 'TssService:pageReadTssHandler', navigateBackFallback } );
			}
		} );
	}

	readTssOnDetailsPage( tssId: string, tssDetailsConfig: TssDetailsConfig ): void {
		const { navigateBack, presetId } = { ...tssDetailsConfig };

		this.uiService.showPageLoaderAndCountUp();

		const readReq$ = this.read( {
			id: tssId,
			includeAdditionalProperties: true,
			presetId,
			skipActionItemChecks: false,
			skipTokenRequest: false
		} );

		this.pageReadTssHandler( readReq$, navigateBack );
	}

	public revealPasswordFromDetail( tss: Tss ): void {

		this.uiService.showPageLoaderAndCountUp();
		this.revealPassword( tss, { updateStore: false } )
		.pipe( take( 1 ) )
		.subscribe( ( tssRequestCompleteEntityState: RequestCompleteEntityState<Tss> ) => {
			if ( tssRequestCompleteEntityState.requestState === RequestState.SUCCESS ) {
				this.storeEntityById$( tss.id )
				.pipe( take( 1 ) )
				.subscribe( ( storeTss: TssVariant ) => {
					tss = {
						...storeTss,
						fccSecret: tssRequestCompleteEntityState.entity.fccSecret
					} as Tss;
					this.storeUpsertOne<TssVariant>( tss );
				} );
			}

			this.uiService.hidePageLoaderOrCountDown();
		} );
	}

	public renewPasswordFromDetail( tss: Tss, dialogWarningI18nPath: string ): void {
		this.dialogCommonService.showConfirmationDialog(
			this.translationService.getI18nString( false, null, dialogWarningI18nPath )
		).pipe(
			take( 1 )
		).subscribe( res => {
			this.uiService.showPageLoaderAndCountUp();
			this.requestPassword( tss, { updateStore: false } )
			.pipe( take( 1 ) )
			.subscribe( ( tssRequestCompleteEntityState: RequestCompleteEntityState<Tss> ) => {

				if ( tssRequestCompleteEntityState.requestState === RequestState.SUCCESS ) {
					this.storeEntityById$( tss.id )
					.pipe( take( 1 ) )
					.subscribe( ( storeTss: TssVariant ) => {
						tss = {
							...storeTss,
							fccSecret: tssRequestCompleteEntityState.entity.fccSecret
						} as Tss;
						this.storeUpsertOne<TssVariant>( tss );
					} );
				}

				this.toasterService.dispatch(
					tssRequestCompleteEntityState,
					this._tssMetadata.entityId,
					actionStatus.REQUEST_NEW_FCC_PASSWORD,
					true
				);

				this.uiService.hidePageLoaderOrCountDown();
			} );
		} );
	}

	public renewFccDatacenterApiKeyFromDetail( tss: Tss, caller: string ): void {
		this.store$.dispatch(
			tssStore.actions.fccDatacenterRenewApiKey(
				{
					caller: `${ caller } | TssService:renewFccDatacenterApiKeyFromDetail`,
					tss
				}
			)
		);
	}

	public triggerFccDatacenterInstallation( tss: TssVariant, caller: string ): void {
		this.store$.dispatch(
			tssStore.actions.fccDatacenterTriggerInstallation(
				{
					caller: `${ caller } | TssService:triggerFccDatacenterInstallation`,
					tss
				}
			)
		);
	}

	showStructureImportForTss( importType: ImportTypeEnum.TSS_EXTERNAL ): void {
		this.store$.dispatch(
			tssStore.actions.structureImportForTss(
				{
					caller: 'TssService:showStructureImportForTss',
					importType
				}
			)
		);
	}

	// CHECKS #################################################################################
	checkAndUpdateEntitiesStates( tssList: TssVariant[], caller: string ): void {
		caller += ' | TssService:checkAndUpdateEntitiesStates';
		tssList.forEach( tss => this.checkAndUpdateEntityState( tss, caller ) );
	}

	checkAndUpdateEntityState( tss: TssVariant, caller: string ): void {
		caller += ' | TssService:checkAndUpdateEntityState';
		this.store$.dispatch(
			tssStore.actions.checkAndUpdateEntityState( { tss, caller } )
		);
	}

	// NAVIGATION #################################################################################
	navigateBack(
		options: {
			caller: string,
			navigateBackFallback?: PresetNavigateBackConfig,
			shouldRestoreListOperationData?: boolean
		}
	): void {
		const {
			caller = '',
			navigateBackFallback,
			shouldRestoreListOperationData = true
		} = options;

		this.logger.debug( options?.caller );

		const fallbackRouteDefinition: RouteDefinition =
			this.routerService.getFallbackRoute(
				navigateBackFallback?.fallbackRoute.basePath,
				navigateBackFallback?.fallbackRoute.path,
				navigateBackFallback?.fallbackRoute.params
			);

		if ( navigateBackFallback?.listOperationsId ) {
			this.listOperationsService.state.listOperationsSetRestoreFlag(
				navigateBackFallback.listOperationsId,
				shouldRestoreListOperationData,
				caller
			);
		}

		this.routerService.navigateBack(
			fallbackRouteDefinition,
			caller + ' | TssService:navigateBack'
		);
	}

	navigateToListPage( presetId?: TssPresetEnum ): void {
		if ( presetId ) {

			let routeDefinition: IRouteDefinition;
			const tssListPageBaseRoute: Omit<TssListPageBaseRoute, 'path'> = {
				basePath: RouteBasePath.Tss
			};

			switch ( presetId ) {
				case TssPresetEnum.TSS:
					const tssListPageRoute: TssListPageRoute = {
						...tssListPageBaseRoute,
						path: TssRoutesEnum.ListPage
					};
					routeDefinition = tssListPageRoute;
					break;

				case TssPresetEnum.TSS_EXTERNAL:
					const tssExternalListPageRoute: TssExternalListPageRoute = {
						...tssListPageBaseRoute,
						path: TssRoutesEnum.ExternalListPage
					};
					routeDefinition = tssExternalListPageRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'TssService:navigateToListPage'
			);
		}
	}

	navigateToDetailPage(
		options: {
			presetId: TssPresetEnum,
			skipStoreUpdate?: boolean,
			skipLocationChange?: boolean,
			skipRoutesComparisonCheck?: boolean,
			tssId: string
		}
	): void {
		const {
			presetId,
			skipStoreUpdate = false,
			skipLocationChange = false,
			skipRoutesComparisonCheck = false,
			tssId
		} = options;

		if ( presetId && tssId ) {

			let routeDefinition: IRouteDefinition;
			const tssDetailPageBaseRoute: Omit<TssDetailPageBaseRoute, 'path'> = {
				basePath: RouteBasePath.Tss,
				params: { tssId }
			};


			switch ( presetId ) {
				case TssPresetEnum.TSS:
					const tssDetailPageRoute: TssDetailPageRoute = {
						...tssDetailPageBaseRoute,
						path: TssRoutesEnum.DetailPage
					};
					routeDefinition = tssDetailPageRoute;
					break;

				case TssPresetEnum.TSS_EXTERNAL:
					const tssExternalDetailPageRoute: TssExternalDetailPageRoute = {
						...tssDetailPageBaseRoute,
						path: TssRoutesEnum.ExternalDetailPage
					};
					routeDefinition = tssExternalDetailPageRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'TssService:navigateToDetailPage',
				skipStoreUpdate,
				skipLocationChange,
				skipRoutesComparisonCheck
			);
		}
	}

	navigateToDetailPageTab(
		options: {
			presetId: TssPresetEnum,
			replaceLastStoredRouteWithNew?: boolean,
			tab: AllowedDetailsTabEnum,
			tssId: string
		}
	): void {
		const {
			presetId,
			replaceLastStoredRouteWithNew = true,
			tab,
			tssId
		} = options;


		if ( presetId && tssId ) {
			let routeDefinition: IRouteDefinition;

			const tssDetailPageTabBaseRoute: Omit<TssDetailPageTabBaseRoute, 'path'> = {
				basePath: RouteBasePath.Tss,
				params: { tssId, tab },
				replaceLastStoredRouteWithNew
			};

			switch ( presetId ) {
				case TssPresetEnum.TSS:
					const tssDetailPageTabRoute: TssDetailPageTabRoute = {
						...tssDetailPageTabBaseRoute,
						path: TssRoutesEnum.DetailPageTab
					};

					routeDefinition = tssDetailPageTabRoute;
					break;

				case TssPresetEnum.TSS_EXTERNAL:
					const tssExternalDetailPageTabRoute: TssExternalDetailPageTabRoute = {
						...tssDetailPageTabBaseRoute,
						path: TssRoutesEnum.ExternalDetailPageTab
					};

					routeDefinition = tssExternalDetailPageTabRoute;
					break;
			}


			this.routerService.navigateToNextRoute(
				routeDefinition,
				'TssService:navigateToDetailPageTab'
			);
		}
	}

	navigateToFormCreate(
		businessPremisesId: string,
		presetId: TssPresetEnum
	): void {
		if ( businessPremisesId && presetId ) {
			let routeDefinition: IRouteDefinition;

			switch ( presetId ) {
				case TssPresetEnum.BUSINESS_PREMISES_DETAIL_TSS_EXTERNAL:
					const businessPremisesDetailPageTssExternalFormPageCreateRoute: BusinessPremisesDetailPageTssExternalFormPageCreateRoute = {
						basePath: RouteBasePath.BusinessPremises,
						path: BusinessPremisesRoutesEnum.DetailPageTssExternalFormPageCreate,
						params: { businessPremisesId, product: ProductEnum.FISCAL }
					};

					routeDefinition = businessPremisesDetailPageTssExternalFormPageCreateRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'ErsService:navigateToFormCreate'
			);
		}
	}

	navigateToFormUpdate( options: { presetId: TssPresetEnum, tssId: string } ): void {
		const {
			presetId,
			tssId
		} = options;

		if ( presetId && tssId ) {
			let routeDefinition: IRouteDefinition;

			switch ( presetId ) {
				case TssPresetEnum.TSS:
					const tssFormPageUpdateRoute: TssFormPageUpdateRoute = {
						basePath: RouteBasePath.Tss,
						path: TssRoutesEnum.FormPageUpdate,
						params: { tssId }
					};

					routeDefinition = tssFormPageUpdateRoute;
					break;

				case TssPresetEnum.TSS_EXTERNAL:
					const tssExternalFormPageUpdateRoute: TssExternalFormPageUpdateRoute = {
						basePath: RouteBasePath.Tss,
						path: TssRoutesEnum.ExternalFormPageUpdate,
						params: { tssId }
					};

					routeDefinition = tssExternalFormPageUpdateRoute;
					break;
			}

			this.routerService.navigateToNextRoute(
				routeDefinition,
				'TssService:navigateToFormUpdate'
			);
		}
	}

	// API #################################################################################
	private executeApiRequestByPresetId<T extends Observable<RequestCompleteEntityState<TssVariant> | RequestCompleteListState<TssVariant>>>(
		presetId: TssPresetEnum,
		tssApi: () => T,
		tssExternalApi: () => T
	): T {
		switch ( presetId ) {
			case TssPresetEnum.TSS:
			case TssPresetEnum.TASK_CENTER_FCC_OPEN_TRANSACTIONS:
			case TssPresetEnum.TASK_CENTER_FCC_VERSIONS:
				return tssApi();

			case TssPresetEnum.BUSINESS_PREMISES_DETAIL_TSS_EXTERNAL:
			case TssPresetEnum.TSS_EXTERNAL:
				return tssExternalApi();
		}
	}


	list( storeConfig?: IStoreConfig, skipActionItemChecks = true ): Observable<RequestCompleteListState<TssVariant>> {
		const storeProcessingConfig = this.createListStoreProcessingConfig<TssVariant>(
			storeConfig,
			this.storeList$(),
			StoreActionsEnum.UPSERT_MANY
		);

		const req$: Observable<RequestCompleteListState<TssVariant>> = this._tssApiService.list();
		const proxyReq$: Observable<RequestCompleteListState<TssVariant>> = this.requestListThroughProxy<TssVariant>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteListState<TssVariant> ) => {
			if ( tssState.requestState === RequestState.SUCCESS && !skipActionItemChecks ) {
				this.checkAndUpdateEntitiesStates( tssState.list, `TssService:list->response` );
			}
		} );

		return proxyReq$;
	}

	searchWithStoredListOperationsData(
		options: {
			caller: string,
			getAllPagesData?: boolean,
			listOperationsId: ListOperationsIdentifierEnum,
			presetId: TssPresetEnum,
			storeConfig?: IStoreConfig
			skipActionItemChecks?: boolean
		}
	): Observable<RequestCompleteListState<TssVariant>> {

		const {
			caller,
			getAllPagesData,
			listOperationsId,
			presetId,
			storeConfig,
			skipActionItemChecks
		} = options;

		const apiReqFn: ( ...args: any[] ) => Observable<RequestCompleteListState<TssVariant>> =
			( [ callerArg, getAllPagesDataArg, presetIdArg, listOperationsArg, skipActionItemChecksArg, storeConfigArg ] ) =>
				this.search(
					{
						caller: callerArg,
						getAllPagesData: getAllPagesDataArg,
						params: listOperationsArg as ITssHttpParams,
						presetId: presetIdArg,
						skipActionItemChecks: skipActionItemChecksArg,
						storeConfig: storeConfigArg
					}
				);

		const args: any[] = [
			`${ caller } | ErsService:searchWithStoredListOperationsData->search`,
			getAllPagesData,
			presetId,
			skipActionItemChecks,
			storeConfig
		];

		const indexForListOperationsArgsArray = 3;

		return this.listOperationsService.helper.requestWithStoredListOperationsData<TssVariant, ITssHttpParams>(
			apiReqFn,
			args,
			indexForListOperationsArgsArray,
			listOperationsId
		);
	}


	// TODO before providing storeConfig param a store search selector is needed
	//  for filtering store entries by params
	/**
	 *
	 * @param options
	 *    caller?: string,
	 * 		getAllPagesData?: boolean
	 * 		params?: ITssHttpParams,
	 * 		presetId: TssPresetEnum,
	 * 		skipActionItemChecks?: boolean,
	 * 		storeConfig?: IStoreConfig
	 */
	search(
		options: {
			caller?: string,
			getAllPagesData?: boolean
			params?: ITssHttpParams,
			presetId: TssPresetEnum,
			skipActionItemChecks?: boolean,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<TssVariant>> {
		const {
			caller,
			getAllPagesData,
			presetId,
			skipActionItemChecks = true,
			storeConfig
		} = options;

		let { params } = options;
		this.logger.debug( caller );


		// default storeConfig setup for search as long as no search selector exists
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createListStoreProcessingConfig<TssVariant>(
			storeConfig ?? defaultStoreConfig,
			this.storeList$(),
			storeConfig?.storeAction ?? StoreActionsEnum.SET_ALL
		);

		params = this.generateParams( params );
		// tslint:disable-next-line:variable-name
		let getAllPagesReq$: ( [ ITssHttpParams ] ) => Observable<RequestCompleteListState<TssVariant>>;
		let variantReq$: Observable<RequestCompleteListState<TssVariant>>;


		switch ( presetId ) {
			case TssPresetEnum.TSS:
				variantReq$ = this._tssApiService.search( { params } );
				getAllPagesReq$ = ( [ paramsArg ] ) => this._tssApiService.search( { params: paramsArg } );
				break;

			case TssPresetEnum.TSS_EXTERNAL:
				variantReq$ = this._tssExternalApiService.search( { params } );
				getAllPagesReq$ = ( [ paramsArg ] ) => this._tssExternalApiService.search( { params: paramsArg } );
				break;
		}

		const req$: Observable<RequestCompleteListState<TssVariant>> = getAllPagesData
																																	 ? this.requestAllPagesData<TssVariant>( getAllPagesReq$, [ params ], 0, 50 )
																																	 : variantReq$;

		const proxyReq$: Observable<RequestCompleteListState<TssVariant>> = this.requestListThroughProxy<TssVariant>( req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteListState<TssVariant> ) => {
			if ( tssState.requestState === RequestState.SUCCESS && !skipActionItemChecks ) {
				this.checkAndUpdateEntitiesStates( tssState.list, `TssService:search->response` );
			}
		} );

		return proxyReq$;
	}

	listByBusinessPremisesId(
		options: {
			caller?: string,
			id: string,
			getAllPagesData?: boolean,
			params?: ITssHttpParams,
			observableReturnType?: ObservableTypeEnum,
			presetId: TssPresetEnum,
			skipActionItemChecks?: boolean,
			storeConfig?: IStoreConfig
		}
	): Observable<RequestCompleteListState<TssVariant>> {
		const {
			caller,
			id,
			getAllPagesData,
			observableReturnType,
			presetId,
			skipActionItemChecks = true,
			storeConfig
		} = options;

		let { params } = options;
		this.logger.debug( caller );

		const storeProcessingConfig = this.createListStoreProcessingConfig<TssVariant>(
			storeConfig,
			this.storeList$(),
			storeConfig?.storeAction ?? StoreActionsEnum.UPSERT_MANY
		);

		params = this.generateParams( params );

		const getAllPagesReq$ = ( [ businessPremisesIdArg, paramsArg ] ) =>
			this.executeApiRequestByPresetId<Observable<RequestCompleteListState<TssVariant>>>(
				presetId,
				() => this._tssApiService.listByBPremisesId( { businessPremisesId: businessPremisesIdArg, params: paramsArg } ),
				() => this._tssExternalApiService.listByBPremisesId( { businessPremisesId: businessPremisesIdArg, params: paramsArg } )
			);

		const req$: Observable<RequestCompleteListState<TssVariant>> =
			getAllPagesData
			? this.requestAllPagesData<TssVariant>( getAllPagesReq$, [ id, params ], 1, 50 )
			: this.executeApiRequestByPresetId<Observable<RequestCompleteListState<TssVariant>>>(
				presetId,
				() => this._tssApiService.listByBPremisesId( { businessPremisesId: id, params } ),
				() => this._tssExternalApiService.listByBPremisesId( { businessPremisesId: id, params } )
			);

		const proxyReq$: Observable<RequestCompleteListState<TssVariant>> = this.requestListThroughProxy<TssVariant>(
			req$,
			storeProcessingConfig,
			observableReturnType
		);

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteListState<TssVariant> ) => {
			if ( tssState.requestState === RequestState.SUCCESS && !skipActionItemChecks ) {
				this.checkAndUpdateEntitiesStates( tssState.list, `TssService:listByBusinessPremisesId->response` );
			}
		} );

		return proxyReq$;
	}

	tssByErsId(
		id: string,
		storeConfig?: IStoreConfig,
		skipActionItemChecks = true,
		includeAdditionalProperties: boolean = false
	): Observable<RequestCompleteEntityState<Tss>> {

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			this.storeEntityById$( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<Tss>> = this._tssApiService.tssByErsId( id, includeAdditionalProperties );
		const proxyReq$: Observable<RequestCompleteEntityState<Tss>> = this.requestEntityThroughProxy<Tss>( id, req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteEntityState<Tss> ) => {
			if ( tssState.requestState === RequestState.SUCCESS && !skipActionItemChecks ) {
				this.checkAndUpdateEntityState( tssState.entity, `TssService:tssByErsId->response` );

			}
		} );

		return proxyReq$;
	}

	listTokens(
		tss: Tss,
		storeConfig?: IStoreConfig,
		skipActionItemChecks = true
	): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<Tss>> = this._tssApiService.listTokens( tss );
		const proxyReq$: Observable<RequestCompleteEntityState<Tss>> = this.requestEntityThroughProxy<Tss>( tss.id, req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteEntityState<Tss> ) => {
			if ( tssState.requestState === RequestState.SUCCESS && !skipActionItemChecks ) {
				this.checkAndUpdateEntityState( tssState.entity, `TssService:listTokens->response` );
			}
		} );

		return proxyReq$;
	}

	read(
		options: {
			enableRequestPreValidation?: boolean,
			id: string,
			includeAdditionalProperties?: boolean,
			observableReturnType?: ObservableTypeEnum,
			presetId: TssPresetEnum,
			skipActionItemChecks?: boolean,
			skipTokenRequest?: boolean,
			storeConfig?: IStoreConfig,
			withFullResponse?: boolean
		}
	): Observable<RequestCompleteEntityState<TssVariant>> {
		const {
			enableRequestPreValidation,
			id,
			includeAdditionalProperties = false,
			observableReturnType,
			presetId,
			skipActionItemChecks = true,
			skipTokenRequest = false,
			withFullResponse
		} = options;

		let {
			storeConfig
		} = options;

		// request from store must be skipped when withFullResponse is set true
		storeConfig = {
			getFromStoreFirst: withFullResponse ? false : storeConfig?.getFromStoreFirst,
			updateStore: storeConfig?.updateStore
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<TssVariant>(
			storeConfig,
			this.storeEntityById$( id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<TssVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			presetId,
			() => this._tssApiService.read( { enableRequestPreValidation, includeAdditionalProperties, tssId: id, withFullResponse } ),
			() => this._tssExternalApiService.read( { enableRequestPreValidation, tssId: id, withFullResponse } )
		);

		const proxyReq$: Observable<RequestCompleteEntityState<TssVariant>> =
			this.requestEntityThroughProxy<TssVariant>( id, req$, storeProcessingConfig, observableReturnType );

		proxyReq$.pipe(
			take( 1 ),
			switchMap( ( tssReadState: RequestCompleteEntityState<TssVariant> ) => {

				return tssReadState.requestState === RequestState.SUCCESS && !skipTokenRequest && [ TssPresetEnum.TSS ].includes( presetId )
							 ? this.listTokens( tssReadState.entity as Tss, { updateStore: false } )
							 .pipe(
								 take( 1 ),
								 map( ( tssTokenState: RequestCompleteEntityState<TssVariant> ) => {
									 this.toasterService.dispatch( tssTokenState, this._tssMetadata.entityId, actionStatus.READ, true );
									 return { ...tssReadState, entity: tssTokenState?.entity };
								 } )
							 )
							 : of( tssReadState );

			} )
		)
		.subscribe( ( tssReadState: RequestCompleteEntityState<TssVariant> ) => {
			if ( tssReadState.requestState === RequestState.SUCCESS && !skipActionItemChecks ) {
				this.checkAndUpdateEntityState( tssReadState.entity, `TssService:read->response` );
			}
		} );

		return proxyReq$;
	}

	requestForBP( tssRequest: TssRequest, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			null,
			StoreActionsEnum.ADD_ONE
		);

		const req$ = this._tssApiService.requestTssFor( tssRequest, RequestTssModalModeEnum.BUSINESS_PREMISES );
		return this.requestEntityThroughProxy<Tss>( null, req$, storeProcessingConfig );
	}

	requestForERS( tssRequest: TssRequest, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			null,
			StoreActionsEnum.ADD_ONE
		);

		const req$ = this._tssApiService.requestTssFor( tssRequest, RequestTssModalModeEnum.ERS );
		return this.requestEntityThroughProxy<Tss>( null, req$, storeProcessingConfig );
	}

	create(
		options: {
			presetId: TssPresetEnum,
			tss: TssVariant,
			storeConfig?: IStoreConfig
		} ): Observable<RequestCompleteEntityState<TssVariant>> {

		const {
			presetId,
			tss,
			storeConfig
		} = options;

		const storeProcessingConfig: IStoreProcessingConfig<TssVariant> = this.createEntityStoreProcessingConfig<TssVariant>(
			storeConfig,
			null,
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<TssVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			presetId,
			() => this._tssApiService.create( { tss: tss as Tss } ),
			() => this._tssExternalApiService.create( { tss: tss as TssExternal } )
		);

		return this.requestEntityThroughProxy<TssVariant>( null, req$, storeProcessingConfig );
	}

	createActivationToken( tss: Tss, storeConfig?: IStoreConfig ): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$ = this._tssApiService.createTokens( tss );
		return this.requestEntityThroughProxy<Tss>( tss.id, req$, storeProcessingConfig );
	}

	update(
		options: {
			tss: TssVariant,
			storeConfig?: IStoreConfig,
			withFullResponse?: boolean,
			presetId: TssPresetEnum
		} ): Observable<RequestCompleteEntityState<TssVariant>> {
		const {
			tss,
			storeConfig,
			withFullResponse = true,
			presetId
		} = options;


		const storeProcessingConfig = this.createEntityStoreProcessingConfig<TssVariant>(
			storeConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);
		const req$: Observable<RequestCompleteEntityState<TssVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			presetId,
			() => this._tssApiService.update( { tss: tss as Tss, withFullResponse } ),
			() => this._tssExternalApiService.update( { tss: tss as TssExternal, withFullResponse } )
		);

		return this.requestEntityThroughProxy<TssVariant>( tss.id, req$, storeProcessingConfig );
	}

	private delete(
		options: {
			presetId: TssPresetEnum,
			storeConfig?: IStoreConfig,
			tssId: string
		}
	): Observable<RequestCompleteEntityState<TssVariant>> {
		const {
			presetId,
			storeConfig,
			tssId
		} = options;

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<TssVariant>(
			storeConfig,
			this.storeEntityById$( tssId ),
			StoreActionsEnum.REMOVE_ONE
		);

		const req$ = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			presetId,
			() => this._tssApiService.delete( { tssId } ),
			() => this._tssExternalApiService.delete( { tssId } )
		);

		return this.requestEntityThroughProxy<TssVariant>( tssId, req$, storeProcessingConfig );
	}

	private changeStatus(
		tss: TssVariant,
		status: TssStatus,
		storeConfig?: IStoreConfig,
		presetId?: TssPresetEnum
	): Observable<RequestCompleteEntityState<TssVariant>> {
		const defaultStoreConfig: IStoreConfig = {
			getFromStoreFirst: false,
			updateStore: true
		};

		const storeProcessingConfig = this.createEntityStoreProcessingConfig<TssVariant>(
			storeConfig ? storeConfig : defaultStoreConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<TssVariant>> = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			presetId,
			() => this._tssApiService.changeStatus( { tss: { ...tss as Tss, status: status as TssStatusEnum } } ),
			() => this._tssExternalApiService.update( { tss: { ...tss as TssExternal, status: status as TssExternalStatusEnum } } )
		);

		return this.requestEntityThroughProxy<TssVariant>( tss.id, req$, storeProcessingConfig );
	}

	tssByFcc( tss: Tss, storeConfig?: IStoreConfig, includeAdditionalProperties: boolean = false ): Observable<RequestCompleteEntityState<Tss>> {
		const storeProcessingConfig = this.createEntityStoreProcessingConfig<Tss>(
			storeConfig,
			this.storeEntityById$( tss.id ),
			StoreActionsEnum.UPSERT_ONE
		);

		const req$: Observable<RequestCompleteEntityState<Tss>> = this._tssApiService.tssByFcc( tss.fccId, includeAdditionalProperties );
		const proxyReq$: Observable<RequestCompleteEntityState<Tss>> = this.requestEntityThroughProxy<Tss>( tss.id, req$, storeProcessingConfig );

		proxyReq$.pipe( take( 1 ) )
		.subscribe( ( tssState: RequestCompleteEntityState<Tss> ) => {
			if ( tssState.requestState === RequestState.SUCCESS ) {
				this.checkAndUpdateEntityState( tssState.entity, `TssService:tssByFcc->response` );
			}
		} );

		return proxyReq$;
	}

	triggerUpload( options: { presetId: TssPresetEnum, tss: TssVariant } ): Observable<RequestCompleteEntityState<TssVariant>> {
		this.uiService.showPageLoaderAndCountUp();
		const req$ = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			options?.presetId,
			() => this._tssApiService.triggerUpload( options?.tss as Tss ),
			() => this._tssExternalApiService.triggerUpload( options?.tss as TssExternal )
		);

		const proxyReq$: Observable<RequestCompleteEntityState<TssVariant>> =
			this.requestEntityThroughProxy<TssVariant>( null, req$, null );

		proxyReq$
		.pipe(
			take( 1 )
		).subscribe( ( tssState: RequestCompleteEntityState<Tss> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			this.toasterService.dispatch( tssState, this._tssMetadata.entityId, actionStatus.TRIGGER_UPLOAD );
		} );

		return proxyReq$;
	}

	triggerErsSync( options: { presetId: TssPresetEnum, storeConfig?: IStoreConfig, tss: TssVariant } ): Observable<RequestCompleteEntityState<TssVariant>> {
		this.uiService.showPageLoaderAndCountUp();
		const req$ = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			options?.presetId,
			() => this._tssApiService.triggerErsSync( options?.tss as Tss ),
			() => this._tssExternalApiService.triggerErsSync( options?.tss as TssExternal )
		);

		const proxyReq$: Observable<RequestCompleteEntityState<TssVariant>> =
			this.requestEntityThroughProxy<TssVariant>( options?.tss.id, req$, null );

		proxyReq$
		.pipe(
			take( 1 )
		).subscribe( ( tssState: RequestCompleteEntityState<Tss> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			this.toasterService.dispatch( tssState, this._tssMetadata.entityId, actionStatus.TRIGGER_ERS_SYNC );
		} );

		return proxyReq$;
	}

	tssVersions( params?: ITssHttpParams ): Observable<RequestCompleteEntityState<TssFccVersions>> {
		params = this.generateParams( params );
		const req$ = this._tssApiService.tssVersions( params );
		return this.requestEntityThroughProxy<TssFccVersions>( null, req$ );
	}

	confirmFinalTssLogExport( options: { presetId: TssPresetEnum, tss: TssVariant } ): Observable<RequestCompleteEntityState<TssVariant>> {
		this.uiService.showPageLoaderAndCountUp();
		const req$ = this.executeApiRequestByPresetId<Observable<RequestCompleteEntityState<TssVariant>>>(
			options?.presetId,
			() => this._tssApiService.confirmFinalTssLogExport( options?.tss as Tss ),
			() => this._tssExternalApiService.confirmFinalTssLogExport( options?.tss as TssExternal )
		);

		const proxyReq$: Observable<RequestCompleteEntityState<TssVariant>> =
			this.requestEntityThroughProxy<TssVariant>( options?.tss.id, req$, null );

		proxyReq$
		.pipe(
			take( 1 )
		).subscribe( ( tssState: RequestCompleteEntityState<Tss> ) => {
			this.uiService.hidePageLoaderOrCountDown();
			this.toasterService.dispatch( tssState, this._tssMetadata.entityId, actionStatus.CONFIRMATION );
		} );

		return proxyReq$;
	}
}
